import { useState, useEffect } from 'react';
import { Bell, Info, CheckCircle, AlertTriangle, AlertCircle, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface Announcement {
  id: string;
  title: string;
  content: string;
  type: 'info' | 'success' | 'warning' | 'danger';
  icon: string | null;
  created_at: string;
}

const typeConfig = {
  info: { icon: Info, color: 'text-primary', bg: 'bg-primary/10' },
  success: { icon: CheckCircle, color: 'text-success', bg: 'bg-success/10' },
  warning: { icon: AlertTriangle, color: 'text-warning', bg: 'bg-warning/10' },
  danger: { icon: AlertCircle, color: 'text-destructive', bg: 'bg-destructive/10' },
};

const NotificationDropdown = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchAnnouncements();
    if (user) {
      fetchReadStatus();
    }
  }, [user]);

  const fetchAnnouncements = async () => {
    const { data, error } = await supabase
      .from('announcements')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(10);

    if (!error && data) {
      setAnnouncements(data as Announcement[]);
    }
  };

  const fetchReadStatus = async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from('user_read_announcements')
      .select('announcement_id')
      .eq('user_id', user.id);

    if (!error && data) {
      setReadIds(new Set(data.map(r => r.announcement_id).filter((id): id is string => id !== null)));
    }
  };

  const markAsRead = async (announcementId: string) => {
    if (!user || readIds.has(announcementId)) return;

    await supabase
      .from('user_read_announcements')
      .insert({ user_id: user.id, announcement_id: announcementId });

    setReadIds(new Set([...Array.from(readIds), announcementId]));
  };

  const markAllAsRead = async () => {
    if (!user) return;

    const unreadAnnouncements = announcements.filter(a => !readIds.has(a.id));
    if (unreadAnnouncements.length === 0) return;

    const inserts = unreadAnnouncements.map(a => ({
      user_id: user.id,
      announcement_id: a.id,
    }));

    await supabase
      .from('user_read_announcements')
      .insert(inserts);

    setReadIds(new Set([...Array.from(readIds), ...unreadAnnouncements.map(a => a.id)]));
  };

  const handleAnnouncementClick = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    markAsRead(announcement.id);
    setIsOpen(false);
  };

  const unreadCount = announcements.filter(a => !readIds.has(a.id)).length;

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-medium">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 glass">
          <div className="p-3 border-b border-border/50 flex items-center justify-between">
            <div>
              <h3 className="font-semibold">Notifications</h3>
              <p className="text-xs text-muted-foreground">{unreadCount} unread</p>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs h-7"
                onClick={(e) => {
                  e.preventDefault();
                  markAllAsRead();
                }}
              >
                Mark all read
              </Button>
            )}
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            {announcements.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                No announcements
              </div>
            ) : (
              announcements.map((announcement) => {
                const config = typeConfig[announcement.type];
                const Icon = config.icon;
                const isUnread = !readIds.has(announcement.id);

                return (
                  <DropdownMenuItem
                    key={announcement.id}
                    onClick={() => handleAnnouncementClick(announcement)}
                    className={cn(
                      "flex items-start gap-3 p-3 cursor-pointer",
                      isUnread && "bg-accent/30"
                    )}
                  >
                    <div className={cn("p-2 rounded-lg", config.bg)}>
                      <Icon className={cn("h-4 w-4", config.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-sm font-medium truncate", isUnread && "font-semibold")}>
                        {announcement.title}
                      </p>
                      <p className="text-xs text-muted-foreground line-clamp-2">
                        {announcement.content}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatDistanceToNow(new Date(announcement.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {isUnread && (
                      <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                    )}
                  </DropdownMenuItem>
                );
              })
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!selectedAnnouncement} onOpenChange={() => setSelectedAnnouncement(null)}>
        <DialogContent className="glass sm:max-w-md">
          {selectedAnnouncement && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3">
                  <div className={cn("p-2 rounded-lg", typeConfig[selectedAnnouncement.type].bg)}>
                    {(() => {
                      const Icon = typeConfig[selectedAnnouncement.type].icon;
                      return <Icon className={cn("h-5 w-5", typeConfig[selectedAnnouncement.type].color)} />;
                    })()}
                  </div>
                  <DialogTitle>{selectedAnnouncement.title}</DialogTitle>
                </div>
                <DialogDescription className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(selectedAnnouncement.created_at), { addSuffix: true })}
                </DialogDescription>
              </DialogHeader>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <p className="text-foreground whitespace-pre-wrap">{selectedAnnouncement.content}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationDropdown;
